import { BriefQueryChannelEnum } from "@api/TrackerApi";
import { proxy } from "valtio";
import { getAdSourceCookie } from "./adSourceCookie.client";

/**
 * ad_source 内存 store（不持久化）。
 * 用途：登录接口上报给后端（后端字段名待确认后接入）。
 * 与 cookie（edgen_ad_source，供 proxy/EntryGate 路由用）并行存在：
 *   - cookie：持久、服务端可读（路由门禁）
 *   - 本 store：内存、不持久化、仅本次会话（登录上报）
 * 写入点：深链捕获 + 登录后 ownerInfo.ad_source 覆盖（与 cookie 同步写）。
 */
const adSourceStore = proxy<{
  adSource: string | null;
  querySource: string | null;
  landingId: string | null;
}>({
  adSource: null,
  querySource: null,
  landingId: null,
});

export function setAdSourceInStore(value: string | null | undefined): void {
  const trimmed = value?.trim();
  adSourceStore.adSource = trimmed ? trimmed.toUpperCase() : null;
}

/**
 * Brief feed `query_channel` 合法取值（对齐 `@api/TrackerApi` `BriefQueryChannelEnum`）。
 * 后端对该参数做枚举校验，未知值 400（04 §5）→ 非枚举值（含 ROAST_HANDOFF 等新 ad_source）
 * 不透传。后端若登记新值，此处与 KMP `BriefApi.BRIEF_QUERY_CHANNELS` 同步加。
 */
const BRIEF_QUERY_CHANNELS: readonly string[] = [
  "ALPHA_PICK",
  "PORTFOLIO_CHECK",
  "SAVING",
  "SAVING_ONBOARDING_SKIP",
  "THEME_RANK",
  "AI_JOB_RISK",
];

/**
 * `query_channel` 枚举守卫（四处 brief 请求传参点统一走这里，替换原 `as BriefQueryChannelEnum` 强转）：
 * 枚举内 true（原始值即合法入参），否则 false → 调用方置 undefined 不带 query_channel。
 * ⚠️ 仅供 query_channel 参数守卫 —— `OS-Ad-Source` 归因头（adjustRequestHeaders）必须照常
 * 透传任意 ad_source 值，不得套用本守卫。
 */
export function isBriefQueryChannel(
  value: string | null | undefined,
): value is BriefQueryChannelEnum {
  return !!value && BRIEF_QUERY_CHANNELS.includes(value.toUpperCase());
}

/**
 * store 原始值（统一大写）；**不套 query_channel 枚举** —— `OS-Ad-Source` 归因头依赖本函数
 * 透传任意值。brief 请求的 `query_channel` 参数走 {@link isBriefQueryChannel} 守卫。
 *
 * 内存为空时**回落读 cookie**（`edgen_ad_source`，持久的那一份）。
 * 不回落会怎样：广告用户登录前刷新一次 → 内存 store 清空 → 上报空 `ad_source` →
 * 后端按 `organic` 兜底 → `firstAdSource` 被**永久锁死为 organic**，整条投放归因作废。
 * 后端的 `firstAdSource` 护栏只是止损，挡不住这个刷新场景。
 *
 * ⚠️ 回落值同样 `toUpperCase()`，与 {@link setAdSourceInStore} 的归一保持一致 ——
 * 否则 {@link isBriefQueryChannel} 的精确匹配会漏。
 */
export function getAdSourceFromStore(): string | null {
  if (adSourceStore.adSource) {
    return adSourceStore.adSource;
  }
  const fromCookie = getAdSourceCookie();
  return fromCookie ? fromCookie.trim().toUpperCase() : null;
}

/** query_source（兴趣分类广告来源）：仅内存 store，不持久化、不存 cookie */
export function setQuerySourceInStore(value: string | null | undefined): void {
  const trimmed = value?.trim();
  adSourceStore.querySource = trimmed ? trimmed : null;
}

export function getQuerySourceFromStore(): string | null {
  return adSourceStore.querySource;
}

/**
 * landing_id（主题榜单广告承接素材主键）：仅内存 store，不持久化、不存 cookie。
 * 生命周期 = 当前前端会话：首请求透传 → 命中卡真实曝光或 miss 后计消（S1.4）。
 * 单独处理：绝不复用会 toUpperCase 的 setAdSourceInStore —— 后端按主键精确匹配，值大小写保真。
 */
export function setLandingIdInStore(value: string | null | undefined): void {
  const trimmed = value?.trim();
  adSourceStore.landingId = trimmed ? trimmed : null;
}

export function getLandingIdFromStore(): string | null {
  return adSourceStore.landingId;
}

/** 读取并计消：返回当前值后立即清空，保证同一 landing_id 只消费一次。 */
export function consumeLandingIdFromStore(): string | null {
  const current = adSourceStore.landingId;
  adSourceStore.landingId = null;
  return current;
}
